import React, { useState } from 'react'
import { Button, Form, Input, Modal, Select, Typography } from 'antd'
import { t } from 'i18next'
import useForm from '../../hooks/use-form'
import { useCreateWebhook } from '../hooks'
import { WebhookWithSecret } from '../webhook'
import { webhookEventTypeLabel, webhookEventTypes } from '../event-type'

type Props = {
    open: boolean
    onCancel: () => void
}

export function CreateWebhookModal({ open, onCancel }: Props) {
    const [createdWebhook, setCreatedWebhook] = useState<WebhookWithSecret | null>(null)
    const mutation = useCreateWebhook()

    const formProps = useForm({
        mutation,
        onSuccess: (webhook) => {
            setCreatedWebhook(webhook)
            formProps.form.resetFields()
        },
    })

    const handleClose = () => {
        setCreatedWebhook(null)
        formProps.form.resetFields()

        onCancel()
    }

    if (createdWebhook) {
        return (
            <Modal
                title={t('Webhook Created')}
                open={open}
                onCancel={handleClose}
                footer={[
                    <Button
                        key="close"
                        onClick={handleClose}
                    >
                        {t('Close')}
                    </Button>,
                ]}
            >
                <div className="space-y-4">
                    <Typography.Text>
                        {t('Your secret key is ready. Copy it now because we will not show it again.')}
                    </Typography.Text>
                    <Typography.Paragraph>
                        <pre>
                            <Typography.Text
                                code
                                copyable
                            >
                                {createdWebhook.secret}
                            </Typography.Text>
                        </pre>
                    </Typography.Paragraph>
                    <Typography.Paragraph type="secondary">
                        {t(
                            'Save this key in your backend and use it to verify the X-Webhook-Signature header on incoming webhook requests.'
                        )}
                    </Typography.Paragraph>
                    <Typography.Paragraph type="secondary">
                        {t('Each webhook request also includes the event type in the X-Webhook-Event header.')}
                    </Typography.Paragraph>
                </div>
            </Modal>
        )
    }

    return (
        <Modal
            title={t('Create Webhook')}
            open={open}
            onCancel={onCancel}
            footer={null}
        >
            <Form
                {...formProps}
                layout="vertical"
                initialValues={{ eventType: 'candidate.created' }}
            >
                <Typography.Paragraph type="secondary">
                    {t('Use an HTTPS URL. Local or internal addresses are not allowed.')}
                </Typography.Paragraph>

                <Form.Item
                    name="url"
                    label={t('Webhook URL')}
                    required
                >
                    <Input placeholder="https://api.example.com/webhooks/receive" />
                </Form.Item>

                <Form.Item
                    name="eventType"
                    label={t('Event')}
                >
                    <Select
                        options={webhookEventTypes.map((eventType) => ({
                            label: webhookEventTypeLabel(eventType),
                            value: eventType,
                        }))}
                    />
                </Form.Item>

                <Form.Item className="mb-0">
                    <div className="flex justify-end space-x-2">
                        <Button onClick={onCancel}>{t('Cancel')}</Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={mutation.isPending}
                        >
                            {t('Create Webhook')}
                        </Button>
                    </div>
                </Form.Item>
            </Form>
        </Modal>
    )
}
